/** Forge Suite shared types — same plane as BookForge / TextForge. */

export type InstanceStatus =
  | "provisioning"
  | "pulling"
  | "loading"
  | "ready"
  | "running"
  | "stopping"
  | "stopped"
  | "error";

export type GpuProvider =
  | "mock"
  | "vast"
  | "runpod"
  | "lambda"
  | "cloudflare"
  | "other"
  | string;

export interface LoadedModelInfo {
  repoId: string;
  name?: string;
  status?: string;
  vramGb?: number;
}

export interface GpuInstance {
  id: string;
  modelRepoId: string;
  modelName: string;
  offerId: string;
  provider: GpuProvider;
  gpuName: string;
  status: InstanceStatus;
  /** OpenAI-compatible base (…/v1) via Forge relay */
  endpointUrl?: string;
  sessionToken?: string;
  pricePerHour: number;
  startedAt?: string;
  error?: string;
  billingActive: boolean;
  provisionMode?: "real" | "mock";
  hubInstanceId?: string;
  hubNote?: string;
  loadedModels?: LoadedModelInfo[];
  activeModelRepoId?: string;
  vramGb?: number;
}

export interface HardwareOffer {
  id: string;
  label?: string;
  gpuName?: string;
  vramGb?: number;
  pricePerHour: number;
  provider?: string;
  available?: boolean;
}

export interface VgpuInstantSku {
  skuId: string;
  hardwareId: string;
  modelSlug: string;
  modelId: string;
  chatModelId?: string;
  name: string;
  paramsB: number;
  provider?: string;
  tags?: string[];
  offer: HardwareOffer;
}

export interface VgpuCatalogResponse {
  configured: boolean;
  stats?: {
    hardware: number;
    models: number;
    combinations: number;
    instantLaunchable: number;
  };
  instant: VgpuInstantSku[];
  hardware?: { id: string; label: string; vramGb: number }[];
  models?: {
    slug: string;
    modelId: string;
    name: string;
    paramsB: number;
    minVramGb: number;
    recommendedFor?: string[];
  }[];
  source?: string;
}

/** BookForge-style Cloudflare inference catalog row */
export interface InferenceCatalogModel {
  skuId: string;
  modelId: string;
  chatModelId?: string;
  name: string;
  modality: string;
  provider?: string;
  paramsB?: number;
  tags?: string[];
  priceInPerMtok?: number;
  priceOutPerMtok?: number;
  connectForRewrite?: boolean;
  offer: {
    id: string;
    pricePerHour: number;
    basePricePerHour?: number;
    available?: boolean;
    provider?: string;
  };
}

export interface InferenceCatalogResponse {
  ok?: boolean;
  configured?: boolean;
  total?: number;
  byModality?: Record<string, number>;
  note?: string;
  models?: InferenceCatalogModel[];
  error?: string;
}

export interface SuiteUser {
  id: string;
  email: string;
  full_name: string | null;
  is_active?: boolean;
  picture_url?: string | null;
}

export interface WalletSnapshot {
  balanceUsd: number;
  currency: string;
  subscriptionActive?: boolean;
  subscriptionLabel?: string | null;
  stripeConfigured?: boolean;
  packs?: { amountUsd: number; label: string }[];
}

export interface SuiteSettings {
  forgeApiUrl: string;
  activeInstanceId: string | null;
}

export const DEFAULT_SUITE_SETTINGS: SuiteSettings = {
  forgeApiUrl: "http://127.0.0.1:8787",
  activeInstanceId: null,
};

/** Prefer vision / agent-friendly models in suite picker */
export const AGENT_MODEL_HINTS = [
  "vision",
  "vl",
  "vlm",
  "qwen2-vl",
  "qwen2.5-vl",
  "qwen3-vl",
  "llava",
  "internvl",
  "phi-3.5-vision",
  "gemini",
  "gpt-4o",
  "computer",
  "ui",
  "agent",
  "multimodal",
  "instruct",
  "chat",
];
